import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const HRBenefitsPage = () => {
  const [activeTab, setActiveTab] = useState('benefits');
  const [benefits, setBenefits] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [childcare, setChildcare] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [employeeFamilyMembers, setEmployeeFamilyMembers] = useState([]);
  const [eligibleChildren, setEligibleChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [summary, setSummary] = useState(null);
  const [formData, setFormData] = useState({
    tenant_id: '',
    employee_id: '',
    benefit_type: 'health',
    benefit_name: '',
    description: '',
    value: '',
    frequency: 'monthly',
    start_date: '',
    end_date: '',
    family_member_id: '',
    coverage: 'individual',
    notes: '',
    status: 'active'
  });
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
    fetchEmployees();
    fetchSummary();
    if (isSuperAdmin) fetchTenants();
  }, [activeTab]);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'benefits') {
        const res = await api.get('/hr/benefits');
        setBenefits(res.data);
      } else if (activeTab === 'family') {
        const res = await api.get('/portal/family?limit=500');
        setFamilyMembers(res.data);
      } else if (activeTab === 'insurance') {
        const res = await api.get('/hr/benefits/insurance');
        setInsurance(res.data);
      } else if (activeTab === 'childcare') {
        const res = await api.get('/hr/benefits/childcare');
        setChildcare(res.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees?limit=500');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/hr/benefits/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchEmployeeFamilyMembers = async (employeeId) => {
    try {
      const response = await api.get(`/portal/family?employee_id=${employeeId}`);
      const members = response.data || [];
      setEmployeeFamilyMembers(members);
      
      // Filter eligible children (under 7 years) for childcare benefit
      const children = members.filter(m => {
        if (!['filho', 'filha', 'child'].includes(m.relationship?.toLowerCase())) return false;
        if (!m.birth_date) return true; // If no birth date, allow selection
        const birthDate = new Date(m.birth_date);
        const age = (new Date() - birthDate) / (365.25 * 24 * 60 * 60 * 1000);
        return age <= 7;
      });
      setEligibleChildren(children);
    } catch (error) {
      console.error('Error fetching family members:', error);
      setEmployeeFamilyMembers([]);
      setEligibleChildren([]);
    }
  };

  const handleEmployeeChange = (employeeId) => {
    setFormData({ ...formData, employee_id: employeeId, family_member_id: '' });
    if (employeeId) {
      fetchEmployeeFamilyMembers(employeeId);
    } else {
      setEmployeeFamilyMembers([]);
      setEligibleChildren([]);
    }
  };

  const handleBenefitTypeChange = (benefitType) => {
    setFormData({ ...formData, benefit_type: benefitType, family_member_id: '' });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate childcare benefit has eligible child
    if (formData.benefit_type === 'childcare') {
      if (eligibleChildren.length === 0) {
        toast.error('Este colaborador não tem filhos elegíveis para cheque creche (idade até 7 anos)');
        return;
      }
      if (!formData.family_member_id) {
        toast.error('Selecione o filho para o cheque creche');
        return;
      }
    }
    
    try {
      const payload = { ...formData };
      if (isSuperAdmin && formData.tenant_id) {
        payload.tenant_id = formData.tenant_id;
      }
      await api.post('/hr/benefits/assign', payload);
      toast.success('Benefício atribuído com sucesso');
      setShowModal(false);
      resetForm();
      fetchData();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atribuir benefício');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingBenefit) return;
    
    try {
      await api.put(`/hr/benefits/${editingBenefit.benefit_id}`, formData);
      toast.success('Benefício atualizado com sucesso');
      setShowEditModal(false);
      setEditingBenefit(null);
      resetForm();
      fetchData();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar benefício');
    }
  };

  const openEditModal = (benefit) => {
    setEditingBenefit(benefit);
    setFormData({
      tenant_id: benefit.tenant_id || '',
      employee_id: benefit.employee_id || '',
      benefit_type: benefit.benefit_type || 'health',
      benefit_name: benefit.benefit_name || '',
      description: benefit.description || '',
      value: benefit.value || '',
      frequency: benefit.frequency || 'monthly',
      start_date: benefit.start_date || '',
      end_date: benefit.end_date || '',
      family_member_id: benefit.family_member_id || '',
      coverage: benefit.coverage || 'individual',
      notes: benefit.notes || '',
      status: benefit.status || 'active'
    });
    if (benefit.employee_id) {
      fetchEmployeeFamilyMembers(benefit.employee_id);
    }
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      tenant_id: '', employee_id: '', benefit_type: 'health', benefit_name: '', description: '',
      value: '', frequency: 'monthly', start_date: '', end_date: '',
      family_member_id: '', coverage: 'individual', notes: '', status: 'active'
    });
    setEmployeeFamilyMembers([]);
    setEligibleChildren([]);
  };

  const handleDelete = async (benefitId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este benefício?')) return;
    try {
      await api.delete(`/hr/benefits/${benefitId}`);
      toast.success('Benefício eliminado');
      fetchData();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar benefício');
    }
  };

  const getEmployeeName = (id) => employees.find(e => e.employee_id === id)?.name || id;
  const getTenantName = (id) => tenants.find(t => t.tenant_id === id)?.name || id;

  const getRelationBadge = (relation) => {
    const labels = { spouse: 'Cônjuge', child: 'Filho(a)', filho: 'Filho', filha: 'Filha', parent: 'Pai/Mãe', sibling: 'Irmão(ã)', other: 'Outro' };
    return labels[relation?.toLowerCase()] || relation;
  };

  const getBenefitTypeBadge = (type) => {
    const colors = {
      health: 'bg-emerald-100 text-emerald-700',
      dental: 'bg-blue-100 text-blue-700',
      vision: 'bg-purple-100 text-purple-700',
      life_insurance: 'bg-amber-100 text-amber-700',
      childcare: 'bg-pink-100 text-pink-700',
      education: 'bg-indigo-100 text-indigo-700',
      meal: 'bg-orange-100 text-orange-700',
      transport: 'bg-teal-100 text-teal-700',
      gym: 'bg-red-100 text-red-700',
      other: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      health: 'Saúde', dental: 'Dentário', vision: 'Visão', life_insurance: 'Seguro Vida',
      childcare: 'Creche', education: 'Educação', meal: 'Alimentação', transport: 'Transporte',
      gym: 'Ginásio', other: 'Outro'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || colors.other}`}>{labels[type] || type}</span>;
  };

  if (loading && activeTab !== 'benefits') {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="hr-benefits-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Benefícios</h1>
          <p className="text-muted-foreground">Benefícios, agregado familiar, seguros e creches</p>
        </div>
        <button 
          onClick={() => setShowModal(true)} 
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid="assign-benefit-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Atribuir Benefício
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{summary?.employees_with_benefits || 0}</p>
          <p className="text-xs text-muted-foreground">Colaboradores c/ Benefícios</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{benefits.length}</p>
          <p className="text-xs text-muted-foreground">Total Benefícios</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{insurance.length || summary?.insurance_policies || 0}</p>
          <p className="text-xs text-muted-foreground">Apólices de Seguro</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{summary?.childcare?.approved || 0}</p>
          <p className="text-xs text-muted-foreground">Vales Creche Ativos</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <p className="text-2xl font-bold">{familyMembers.length}</p>
          <p className="text-xs text-muted-foreground">Membros do Agregado</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border overflow-x-auto">
        <button onClick={() => setActiveTab('benefits')} className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'benefits' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Benefícios</button>
        <button onClick={() => setActiveTab('family')} className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'family' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Agregado Familiar</button>
        <button onClick={() => setActiveTab('insurance')} className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'insurance' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Seguros</button>
        <button onClick={() => setActiveTab('childcare')} className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'childcare' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'}`}>Vales Creche</button>
      </div>

      {/* Benefits Tab */}
      {activeTab === 'benefits' && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  {isSuperAdmin && <th className="px-4 py-3 text-left text-sm font-medium">Tenant</th>}
                  <th className="px-4 py-3 text-left text-sm font-medium">Colaborador</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Nome</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Valor</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Frequência</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Cobertura</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Estado</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {benefits.length === 0 ? (
                  <tr><td colSpan={isSuperAdmin ? 9 : 8} className="px-4 py-8 text-center text-muted-foreground">Sem benefícios atribuídos</td></tr>
                ) : (
                  benefits.map((item, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      {isSuperAdmin && (
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            {getTenantName(item.tenant_id)}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm font-medium">{item.employee_name || getEmployeeName(item.employee_id)}</td>
                      <td className="px-4 py-3 text-sm">{getBenefitTypeBadge(item.benefit_type)}</td>
                      <td className="px-4 py-3 text-sm">{item.benefit_name || '-'}</td>
                      <td className="px-4 py-3 text-sm">{item.value ? `${item.value}€` : '-'}</td>
                      <td className="px-4 py-3 text-sm">{item.frequency === 'monthly' ? 'Mensal' : item.frequency === 'yearly' ? 'Anual' : 'Único'}</td>
                      <td className="px-4 py-3 text-sm">{item.coverage === 'family' ? 'Familiar' : 'Individual'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                          {item.status === 'active' ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm space-x-2">
                        <button 
                          onClick={() => openEditModal(item)}
                          className="text-blue-600 hover:text-blue-800"
                          data-testid={`edit-benefit-${item.benefit_id}`}
                        >
                          Editar
                        </button>
                        <button 
                          onClick={() => handleDelete(item.benefit_id)}
                          className="text-red-600 hover:text-red-800"
                          data-testid={`delete-benefit-${item.benefit_id}`}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Family Members Tab */}
      {activeTab === 'family' && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Colaborador</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Nome</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Parentesco</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Data Nasc.</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">NIF</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Dependente</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {familyMembers.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sem registos</td></tr>
                ) : (
                  familyMembers.map((member, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">{getEmployeeName(member.employee_id)}</td>
                      <td className="px-4 py-3 text-sm font-medium">{member.name}</td>
                      <td className="px-4 py-3 text-sm">{getRelationBadge(member.relationship)}</td>
                      <td className="px-4 py-3 text-sm">{member.birth_date || '-'}</td>
                      <td className="px-4 py-3 text-sm font-mono">{member.tax_id || '-'}</td>
                      <td className="px-4 py-3 text-sm">{member.is_dependent ? 'Sim' : 'Não'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Insurance Tab */}
      {activeTab === 'insurance' && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Colaborador</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Tipo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Seguradora</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Apólice</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Início</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Fim</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Capital</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {insurance.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Sem apólices</td></tr>
                ) : (
                  insurance.map((item, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">{getEmployeeName(item.employee_id)}</td>
                      <td className="px-4 py-3 text-sm">{item.insurance_type}</td>
                      <td className="px-4 py-3 text-sm">{item.provider}</td>
                      <td className="px-4 py-3 text-sm font-mono">{item.policy_number}</td>
                      <td className="px-4 py-3 text-sm">{item.start_date}</td>
                      <td className="px-4 py-3 text-sm">{item.end_date}</td>
                      <td className="px-4 py-3 text-sm">{item.coverage_amount ? `${item.coverage_amount}€` : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Childcare Tab */}
      {activeTab === 'childcare' && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Colaborador</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Criança</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Creche</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Valor Mensal</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {childcare.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Sem vales creche</td></tr>
                ) : (
                  childcare.map((item, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">{getEmployeeName(item.employee_id)}</td>
                      <td className="px-4 py-3 text-sm">{item.child_name}</td>
                      <td className="px-4 py-3 text-sm">{item.childcare_provider}</td>
                      <td className="px-4 py-3 text-sm">{item.monthly_amount ? `${item.monthly_amount}€` : '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'active' || item.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                          {item.status === 'active' || item.status === 'approved' ? 'Ativo' : item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Benefit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Atribuir Benefício</h2>
              <p className="text-sm text-muted-foreground">Atribuir um benefício a um colaborador</p>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {/* Tenant selection for Super Admin */}
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select
                    value={formData.tenant_id}
                    onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="">Selecionar Tenant</option>
                    {tenants.map(t => (
                      <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Colaborador *</label>
                <select 
                  value={formData.employee_id} 
                  onChange={(e) => handleEmployeeChange(e.target.value)} 
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background" 
                  required
                >
                  <option value="">Selecionar colaborador</option>
                  {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.name}</option>)}
                </select>
              </div>

              {/* Show family members info for context */}
              {formData.employee_id && employeeFamilyMembers.length > 0 && (
                <div className="p-3 rounded-lg bg-muted/50 border border-border">
                  <p className="text-sm font-medium mb-2">Agregado Familiar ({employeeFamilyMembers.length})</p>
                  <div className="space-y-1">
                    {employeeFamilyMembers.map(m => (
                      <div key={m.member_id} className="text-xs flex items-center gap-2">
                        <span className="font-medium">{m.name}</span>
                        <span className="text-muted-foreground">({getRelationBadge(m.relationship)})</span>
                        {m.birth_date && <span className="text-muted-foreground">- {m.birth_date}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de Benefício *</label>
                  <select 
                    value={formData.benefit_type} 
                    onChange={(e) => handleBenefitTypeChange(e.target.value)} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="health">Saúde</option>
                    <option value="dental">Dentário</option>
                    <option value="vision">Visão</option>
                    <option value="life_insurance">Seguro Vida</option>
                    <option value="childcare">Creche</option>
                    <option value="education">Educação</option>
                    <option value="meal">Alimentação</option>
                    <option value="transport">Transporte</option>
                    <option value="gym">Ginásio</option>
                    <option value="other">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cobertura</label>
                  <select 
                    value={formData.coverage} 
                    onChange={(e) => setFormData({ ...formData, coverage: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="individual">Individual</option>
                    <option value="family">Familiar</option>
                  </select>
                </div>
              </div>

              {/* Childcare validation warning */}
              {formData.benefit_type === 'childcare' && formData.employee_id && eligibleChildren.length === 0 && (
                <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700">
                  <p className="text-sm text-amber-800 dark:text-amber-200 font-medium flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Este colaborador não tem filhos elegíveis para cheque creche (idade até 7 anos)
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Nome do Benefício</label>
                <input 
                  type="text" 
                  value={formData.benefit_name} 
                  onChange={(e) => setFormData({ ...formData, benefit_name: e.target.value })} 
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  placeholder="Ex: Seguro de Saúde Multicare"
                />
              </div>

              {/* Family member selection for childcare */}
              {formData.benefit_type === 'childcare' && eligibleChildren.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Filho(a) para o Cheque Creche *</label>
                  <select 
                    value={formData.family_member_id} 
                    onChange={(e) => setFormData({ ...formData, family_member_id: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="">Selecionar filho(a)</option>
                    {eligibleChildren.map(m => (
                      <option key={m.member_id} value={m.member_id}>
                        {m.name} {m.birth_date ? `(${m.birth_date})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Family member for other benefit types */}
              {['education', 'family_health'].includes(formData.benefit_type) && employeeFamilyMembers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Membro do Agregado</label>
                  <select 
                    value={formData.family_member_id} 
                    onChange={(e) => setFormData({ ...formData, family_member_id: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="">Selecionar membro</option>
                    {employeeFamilyMembers.map(m => (
                      <option key={m.member_id} value={m.member_id}>
                        {m.name} ({getRelationBadge(m.relationship)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Valor (€)</label>
                  <input 
                    type="number" 
                    value={formData.value} 
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Frequência</label>
                  <select 
                    value={formData.frequency} 
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="monthly">Mensal</option>
                    <option value="yearly">Anual</option>
                    <option value="one-time">Único</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Data Início</label>
                  <input 
                    type="date" 
                    value={formData.start_date} 
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data Fim</label>
                  <input 
                    type="date" 
                    value={formData.end_date} 
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notas</label>
                <textarea 
                  value={formData.notes} 
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  rows={2}
                  placeholder="Observações adicionais"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  onClick={() => { setShowModal(false); resetForm(); }} 
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  disabled={formData.benefit_type === 'childcare' && eligibleChildren.length === 0}
                >
                  Atribuir Benefício
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Benefit Modal */}
      {showEditModal && editingBenefit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Editar Benefício</h2>
              <p className="text-sm text-muted-foreground">Atualizar informações do benefício</p>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4">
              {/* Tenant info for Super Admin (read-only) */}
              {isSuperAdmin && editingBenefit.tenant_id && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant</label>
                  <input
                    type="text"
                    value={getTenantName(editingBenefit.tenant_id)}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-muted text-muted-foreground"
                    disabled
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Colaborador</label>
                <input 
                  type="text" 
                  value={editingBenefit.employee_name || getEmployeeName(editingBenefit.employee_id)} 
                  className="w-full px-3 py-2 rounded-lg border border-input bg-muted text-muted-foreground"
                  disabled
                />
              </div>

              {/* Show family members info for context */}
              {employeeFamilyMembers.length > 0 && (
                <div className="p-3 rounded-lg bg-muted/50 border border-border">
                  <p className="text-sm font-medium mb-2">Agregado Familiar ({employeeFamilyMembers.length})</p>
                  <div className="space-y-1">
                    {employeeFamilyMembers.map(m => (
                      <div key={m.member_id} className="text-xs flex items-center gap-2">
                        <span className="font-medium">{m.name}</span>
                        <span className="text-muted-foreground">({getRelationBadge(m.relationship)})</span>
                        {m.birth_date && <span className="text-muted-foreground">- {m.birth_date}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de Benefício</label>
                  <select 
                    value={formData.benefit_type} 
                    onChange={(e) => handleBenefitTypeChange(e.target.value)} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="health">Saúde</option>
                    <option value="dental">Dentário</option>
                    <option value="vision">Visão</option>
                    <option value="life_insurance">Seguro Vida</option>
                    <option value="childcare">Creche</option>
                    <option value="education">Educação</option>
                    <option value="meal">Alimentação</option>
                    <option value="transport">Transporte</option>
                    <option value="gym">Ginásio</option>
                    <option value="other">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cobertura</label>
                  <select 
                    value={formData.coverage} 
                    onChange={(e) => setFormData({ ...formData, coverage: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="individual">Individual</option>
                    <option value="family">Familiar</option>
                  </select>
                </div>
              </div>

              {/* Childcare validation warning */}
              {formData.benefit_type === 'childcare' && eligibleChildren.length === 0 && (
                <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700">
                  <p className="text-sm text-amber-800 dark:text-amber-200 font-medium flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Este colaborador não tem filhos elegíveis para cheque creche
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Nome do Benefício</label>
                <input 
                  type="text" 
                  value={formData.benefit_name} 
                  onChange={(e) => setFormData({ ...formData, benefit_name: e.target.value })} 
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  placeholder="Ex: Seguro de Saúde Multicare"
                />
              </div>

              {/* Family member selection for childcare */}
              {formData.benefit_type === 'childcare' && eligibleChildren.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Filho(a) para o Cheque Creche *</label>
                  <select 
                    value={formData.family_member_id} 
                    onChange={(e) => setFormData({ ...formData, family_member_id: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="">Selecionar filho(a)</option>
                    {eligibleChildren.map(m => (
                      <option key={m.member_id} value={m.member_id}>
                        {m.name} {m.birth_date ? `(${m.birth_date})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Valor (€)</label>
                  <input 
                    type="number" 
                    value={formData.value} 
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Frequência</label>
                  <select 
                    value={formData.frequency} 
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="monthly">Mensal</option>
                    <option value="yearly">Anual</option>
                    <option value="one-time">Único</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Data Início</label>
                  <input 
                    type="date" 
                    value={formData.start_date} 
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data Fim</label>
                  <input 
                    type="date" 
                    value={formData.end_date} 
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} 
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Estado</label>
                <select 
                  value={formData.status || 'active'} 
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                  <option value="pending">Pendente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notas</label>
                <textarea 
                  value={formData.notes} 
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  rows={2}
                  placeholder="Observações adicionais"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  onClick={() => { setShowEditModal(false); setEditingBenefit(null); resetForm(); }} 
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  data-testid="save-benefit-btn"
                >
                  Guardar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRBenefitsPage;
